import React, { useEffect, useState } from "react";
import "../ChatHistory/ChatHistory.scss";
import IntroJs from "intro.js";
import "intro.js/minified/introjs.min.css";
import {
  Button,
  List,
  ListItem,
} from "@mui/material";
import { PrimaryButton } from "../AllButtons/AllButtons";
import { useAppDispatch, useAppSelector } from "../../Store/store";
import { getUserConversations, renameConversation } from "../../Services/chatbox.service"
import ConversationListItem from "../CommonElements/ConversationList/ConversationList";
import DeleteCommonDialog from "../DeleteCommonDialog/DeleteCommonDialog";
import { useNavigate } from "react-router-dom";
import { openSnackbar } from "../../Slice/snackbarSlice";
import { getErrorMessage } from "../../Utils/errorMessage";

const HISTORY_GROUP_ORDER = ["Today", "Yesterday", "Last Week", "Last Month"];

interface HistoryProps {
  isHistoryOpen: boolean;
  onHistoryToggle: () => void;
  onConversationSelect: (conversationId: string) => void;
  onStartChat?: (startChat: boolean) => void;
  currentConversationId: string | null;
  toggleTrySuggestion: boolean;
  setToggleTrySuggestion: React.Dispatch<React.SetStateAction<boolean>>;
}

interface Conversation {
  conversation_id: string;
  subject_id: number;
  title: string;
  formatted_date?: string;
  created_at: string;
  updated_at?: string | null;
  last_activity_at?: string | null;
  messages?: Array<{
    prompt: string;
    ai_response: {
      response: string;
    };
    created_at: string;
  }>;
}

const getConversationLastActivity = (conversation: Conversation) => {
  const timestamps = [
    conversation.last_activity_at,
    conversation.updated_at,
    conversation.created_at,
    ...(conversation.messages || []).map((message) => message.created_at),
  ]
    .filter(Boolean)
    .map((timestamp) => new Date(timestamp as string).getTime())
    .filter((timestamp) => !Number.isNaN(timestamp));

  if (timestamps.length === 0) {
    return conversation.created_at;
  }

  return new Date(Math.max(...timestamps)).toISOString();
};

const getRelativeDateLabel = (timestamp: string) => {
  const activityDate = new Date(timestamp);
  if (Number.isNaN(activityDate.getTime())) {
    return null;
  }

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfActivityDate = new Date(
    activityDate.getFullYear(),
    activityDate.getMonth(),
    activityDate.getDate()
  );
  const daysDiff = Math.floor(
    (startOfToday.getTime() - startOfActivityDate.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (daysDiff <= 0) {
    return "Today";
  }
  if (daysDiff === 1) {
    return "Yesterday";
  }
  if (daysDiff < 7) {
    return "Last Week";
  }
  if (daysDiff < 30) {
    return "Last Month";
  }

  return null;
};

const ChatHistory: React.FC<HistoryProps> = ({
  isHistoryOpen,
  onHistoryToggle,
  onConversationSelect,
  onStartChat,
  currentConversationId,
  toggleTrySuggestion,
  setToggleTrySuggestion
}) => {
  const [conversationId, setConversationId] = useState(""); // Fixed typo
  const [deleteModalClicked, setDeleteModalClicked] = useState(false);
  const [moreMenu, setMoreMenu] = React.useState<{
    id: null | string;
    element: null | HTMLElement;
  }>({ id: null, element: null });
  const isMenuOpen = (id: string) => moreMenu.id === id;

    const userdetail = useAppSelector(state => state.user.user);
  const user = useAppSelector(state => state.user);
  const tokens = useAppSelector(state => state.user.user.remaining_tokens);
  const isPaid=useAppSelector(state=>state.user.user.is_paid);
  const conversations = useAppSelector(state => state.chatai.conversations) || [];
  const [activeConversationId, setActiveConversationId] = useState("");

  const handleClickMoreMenu = (event: any, conversation: any) => {
    setMoreMenu({ id: conversation.conversation_id, element: event.currentTarget });
  };

  const handleCloseMoreMenu = () => {
    setMoreMenu({ id: null, element: null });
  };

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const remainingUsage =
    user?.tokensRemaining === "" ||
    user?.tokensRemaining === null ||
    user?.tokensRemaining === undefined
      ? tokens
      : user.tokensRemaining;

  const handleConversationFetch = async () => {
    const studentId = user.uid || localStorage.getItem("id");
    if (!studentId) {
      return;
    }

    try {
      await dispatch(getUserConversations(studentId)).unwrap();
    } catch (error) {
      console.error("Error fetching conversations", error);
    }
  };

  useEffect(() => {
    handleConversationFetch();
  }, [toggleTrySuggestion, user.tokensRemaining, user.uid]);
  useEffect(() => {
  const intro = IntroJs();
  if (localStorage.getItem("tour")) {
    intro.setOptions({
      steps: [
        {
          element: ".step-1",
          intro: `Start a new chat here`,
        },
        {
          element: ".step-2",
          intro: "View and manage chat history.",
        },
        {
          element: ".step-3",
          intro: "Available tokens here and upgrade for more",
        },
      ],
      showStepNumbers: false,
      exitOnOverlayClick: false,
      nextLabel: "Next",
      prevLabel: "Skip",
      skipLabel: "Skip",
      keyboardNavigation: true,
      showButtons: true,
      overlayOpacity: 0.9,
      exitOnEsc: true,
      disableInteraction: true, // Prevent interaction with elements
    });

    // Simple approach: Just override the previous button click
    intro.onbeforechange(() => {
      // Wait for DOM to be ready
      requestAnimationFrame(() => {
        const prevButton = document.querySelector('.introjs-prevbutton');
        if (prevButton) {
          // Change text
          prevButton.textContent = 'Skip';
          
          // Add click event listener with high priority
          prevButton.setAttribute('onclick', '');
          prevButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopImmediatePropagation();
            intro.exit(true);
          }, { capture: true });
        }
      });
      return true;
    });

    intro.start();
  }
  localStorage.removeItem("tour");
}, []);
  const handleShowConversation = (conversationId: any) => {
    onStartChat?.(false);
    onConversationSelect(conversationId);
    setActiveConversationId(conversationId);
  }

  const handleStartNewChat = () => {
    onConversationSelect("");
    setTimeout(() => {
      onStartChat?.(true);
    }, 10);
    setActiveConversationId("");
    setToggleTrySuggestion(false);
  }

  const handleDeleteConversation = (conversationId: any) => {
    setConversationId(conversationId);
    setDeleteModalClicked(true);
  }

  const handleConversationDeleted = () => {
    handleConversationFetch();

    if (
      conversationId === currentConversationId ||
      conversationId === localStorage.getItem("conver_ID")
    ) {
      localStorage.removeItem("conver_ID");
      handleStartNewChat();
    }
  }

  const handleRename = async (id: string, newTitle: string) => {
    const normalizedTitle = newTitle.trim();
    if (!normalizedTitle) {
      return;
    }

    const renameObj = {
      conversation_Id: id,
      renameTitle: {
        new_title: normalizedTitle
      }
    };

    try {
      await dispatch(renameConversation(renameObj)).unwrap();
      handleConversationFetch();
    } catch (error) {
      dispatch(
        openSnackbar({
          message: getErrorMessage(error, "Failed to rename chat title."),
          severity: "error",
        })
      );
    }
  };

  const normalizedConversations = conversations
    .map((conversation) => {
      const lastActivityAt = getConversationLastActivity(conversation);
      return {
        ...conversation,
        last_activity_at: lastActivityAt,
        formatted_date:
          getRelativeDateLabel(lastActivityAt) || conversation.formatted_date || "Last Month",
      };
    })
    .sort((a, b) => {
      const firstTimestamp = new Date(a.last_activity_at || a.created_at).getTime();
      const secondTimestamp = new Date(b.last_activity_at || b.created_at).getTime();
      return secondTimestamp - firstTimestamp;
    });

  const groupLabels = [
    ...HISTORY_GROUP_ORDER.filter((label) =>
      normalizedConversations.some((conversation) => conversation.formatted_date === label)
    ),
    ...Array.from(
      new Set(
        normalizedConversations
          .map((conversation) => conversation.formatted_date)
          .filter(
            (label): label is string => !!label && !HISTORY_GROUP_ORDER.includes(label)
          )
      )
    ),
  ];
  return (
    <>
      <div className={`chat-history-wrapper ${isHistoryOpen ? "open" : ""}`}>
        <div className="chat-history-header step-1">
          <Button
            className="new-chat-btn"
            onClick={handleStartNewChat}
            data-testid="start-new-chat-btn"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M18 13H13V18C13 18.55 12.55 19 12 19C11.45 19 11 18.55 11 18V13H6C5.45 13 5 12.55 5 12C5 11.45 5.45 11 6 11H11V6C11 5.45 11.45 5 12 5C12.55 5 13 5.45 13 6V11H18C18.55 11 19 11.45 19 12C19 12.55 18.55 13 18 13Z"
                fill="#4F46E5"
              />
            </svg>
            Start Chat
          </Button>
        </div>
        <div className="chat-history-list step-2">
          <div className="chat-history-lable"><span>Today</span></div>
          <div>
            <List className="history-list ">
              {normalizedConversations.filter(
                (conversation) => conversation.formatted_date === "Today"
              ).length === 0 ? (
                <ListItem><span>New Chat</span></ListItem>
              ) : (
                normalizedConversations
                  .filter((conversation) => conversation.formatted_date === "Today")
                  .map((conversation) => (
                    <ConversationListItem
                      key={conversation.conversation_id}
                      conversation={conversation}
                      handleClickMoreMenu={handleClickMoreMenu}
                      handleCloseMoreMenu={handleCloseMoreMenu}
                      handleDeleteConversation={handleDeleteConversation}
                      handleRenameConversation={handleRename}
                      handleGetConversationId={handleShowConversation}
                      moreMenu={moreMenu}
                      open={isMenuOpen(conversation.conversation_id)}
                      activeConversationId={activeConversationId}
                      currentConversationId={currentConversationId}
                    />
                  ))
              )}
            </List>
          </div>
          {groupLabels
            .filter((label) => label !== "Today")
            .map((label) => (
              <React.Fragment key={label}>
                <div className="chat-history-lable"><span>{label}</span></div>
                <div>
                  <List className="history-list">
                    {normalizedConversations
                      .filter((conversation) => conversation.formatted_date === label)
                      .map((conversation, index) => (
                        <ConversationListItem
                          key={`${conversation.conversation_id}-${index}`}
                          conversation={conversation}
                          handleClickMoreMenu={handleClickMoreMenu}
                          handleCloseMoreMenu={handleCloseMoreMenu}
                          handleDeleteConversation={handleDeleteConversation}
                          handleGetConversationId={handleShowConversation}
                          handleRenameConversation={handleRename}
                          moreMenu={moreMenu}
                          open={isMenuOpen(conversation.conversation_id)}
                          activeConversationId={activeConversationId}
                          currentConversationId={currentConversationId}
                        />
                      ))}
                  </List>
                </div>
              </React.Fragment>
            ))}
        </div>
        <div className="chat-history-footer">
          <div className="token-upgread-block step-3">
            <div className="token-badge">
              <div className="token-badge-label">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M7.50004 3.3335C3.81671 3.3335 0.833374 6.31683 0.833374 10.0002C0.833374 13.6835 3.81671 16.6668 7.50004 16.6668C11.1834 16.6668 14.1667 13.6835 14.1667 10.0002C14.1667 6.31683 11.1834 3.3335 7.50004 3.3335ZM7.50004 15.0002C4.74171 15.0002 2.50004 12.7585 2.50004 10.0002C2.50004 7.24183 4.74171 5.00016 7.50004 5.00016C10.2584 5.00016 12.5 7.24183 12.5 10.0002C12.5 12.7585 10.2584 15.0002 7.50004 15.0002ZM10 8.75016H8.33337V12.9168H6.66671V8.75016H5.00004V7.50016H10V8.75016ZM16.875 3.12516L19.1667 4.16683L16.875 5.2085L15.8334 7.50016L14.7917 5.2085L12.5 4.16683L14.7917 3.12516L15.8334 0.833496L16.875 3.12516ZM16.875 14.7918L19.1667 15.8335L16.875 16.8752L15.8334 19.1668L14.7917 16.8752L12.5 15.8335L14.7917 14.7918L15.8334 12.5002L16.875 14.7918Z"
                    fill="#4F46E5"
                  />
                </svg>

                <span>Tokens</span>
              </div>
              <div className="token-badge-value">{remainingUsage}</div>
            </div>

            {!isPaid&&
            <PrimaryButton onClick={() => {
                  // handleClose();
                  if (!userdetail.is_paid && userdetail.remaining_tokens < 1) {
                     navigate('/price')
                     } else {
                    navigate('/dashboard/plan_details')
                  }
                }}>Upgrade to get more</PrimaryButton>}
          </div>
        </div>
        {deleteModalClicked &&
          <DeleteCommonDialog
            converstationId={conversationId}
            isDeleteOpen={deleteModalClicked}
            onClose={() => setDeleteModalClicked(false)}
            handleGetConversation={handleConversationDeleted}
          />
        }
      </div>
    </>
  );
};

export default ChatHistory;
